import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {BehaviorSubject} from 'rxjs';
import {URLS} from '../../environments/environment';

/**
 * User currency preference + ILS -> currency conversion using live rates.
 *
 * The preference is stored client-side (localStorage 'currency'), mirroring how
 * the language preference is handled. Rates come from the same public feed the
 * calculator uses (currency/getLatest); each entry's `value` is the ILS price of
 * 1 unit of that currency (e.g. USD value 2.97 => 1 USD = 2.97 ILS, ILS value 1).
 */
@Injectable({providedIn: 'root'})
export class CurrencyService {
  // Common currencies surfaced first in the picker; every other currency from
  // the rate feed follows alphabetically.
  static readonly MAJORS = ['ils', 'usd', 'eur', 'gbp', 'aed'];

  private rates: any[] = [];            // [{ name, value }], value = ILS per 1 unit
  private synced = false;               // server preference pulled once per session
  private loading = false;              // a rate fetch is in flight
  private _options: string[] = [...CurrencyService.MAJORS];  // picker list; recomputed once rates load
  private _preferred = new BehaviorSubject<string>(this.initialPreferred());
  readonly preferred$ = this._preferred.asObservable();

  // Default when the user has not explicitly chosen a currency. SlickChain is an
  // Israeli-first platform, so ILS is the default; foreign users set their
  // currency explicitly (and non-ILS then drives the English/foreign experience).
  private defaultCurrency(): string {
    return 'ils';
  }

  private initialPreferred(): string {
    return (localStorage.getItem('currency') || this.defaultCurrency()).toLowerCase();
  }

  constructor(private http: HttpClient) {
    this.load();
  }

  // currency/getLatest responds with { code, data: [{ name, value }, ...] } and
  // requires auth, so the first attempt can run before login — ensureLoaded() retries.
  private load(attempt = 0): void {
    if (this.loading) { return; }
    this.loading = true;
    this.http.get<any>(URLS.API + 'currency/getLatest').subscribe(
      (res) => { this.loading = false; this.rates = (res && res.data) || []; this._options = this.computeOptions(); },
      () => {
        this.loading = false;
        // The feed needs auth; early attempts can run before the token is ready — retry briefly.
        if (attempt < 4) { setTimeout(() => this.load(attempt + 1), 1500); }
      }
    );
  }

  /** Fetch rates if not yet loaded (e.g. the first attempt ran before auth was ready). */
  ensureLoaded(): void {
    if (!this.rates.length) { this.load(); }
  }

  /** All currencies from the rate feed — majors first, then the rest alphabetically (stable ref). */
  get options(): string[] { return this._options; }

  private computeOptions(): string[] {
    const all = this.rates
      .map(r => (r.name || '').toLowerCase())
      .filter(n => /^[a-z]{3}$/.test(n));
    if (!all.length) {
      // Rates not loaded yet — at least offer the majors and the current choice.
      return Array.from(new Set([...CurrencyService.MAJORS, this.preferred]));
    }
    const set = new Set(all);
    const majors = CurrencyService.MAJORS.filter(m => set.has(m));
    const rest = Array.from(set).filter(n => !majors.includes(n)).sort();
    return [...majors, ...rest];
  }
  get preferred(): string { return this._preferred.value; }

  setPreferred(name: string): void {
    const n = (name || 'ils').toLowerCase();
    localStorage.setItem('currency', n);
    this._preferred.next(n);
  }

  /** Apply locally (immediate) and persist to the user's profile (best-effort). */
  choose(name: string): void {
    this.setPreferred(name);
    this.saveServerCurrency(name).subscribe(() => {}, () => {});
  }

  fetchServerCurrency() {
    return this.http.get<{ code: number; data: { currency: string | null } }>(URLS.API + 'users/currency');
  }

  saveServerCurrency(currency: string) {
    return this.http.patch<{ code: number; data: { currency: string } }>(URLS.API + 'users/currency', { currency });
  }

  /** Pull the authoritative preference from the user's profile and apply it locally (once). */
  syncFromServer(): void {
    if (this.synced) { return; }
    this.synced = true;
    this.fetchServerCurrency().subscribe(
      (res) => { if (res?.code === 200 && res.data?.currency) { this.setPreferred(res.data.currency); } },
      () => { this.synced = false; }   // allow retry if it failed (e.g. not authenticated yet)
    );
  }

  private rate(name: string): number | null {
    const cur = this.rates.find(c => (c.name || '').toLowerCase() === name);
    return cur && cur.value ? Number(cur.value) : null;
  }

  /** True when the preferred currency can be rendered (ILS, or its rate is loaded). */
  private hasRate(): boolean {
    return this.preferred === 'ils' || this.rate(this.preferred) !== null;
  }

  /** ISO code for the Angular currency pipe; falls back to ILS until rates load. */
  get code(): string {
    return (this.hasRate() ? this.preferred : 'ils').toUpperCase();
  }

  private static readonly SYMBOLS: { [k: string]: string } = {
    ils: '₪', usd: '$', eur: '€', gbp: '£', aed: 'د.إ', ngn: '₦', nio: 'C$',
    cad: 'C$', aud: 'A$', jpy: '¥', cny: '¥', inr: '₹', zar: 'R', brl: 'R$',
    rub: '₽', try: '₺', krw: '₩', mxn: '$'
  };
  /** Display symbol for the active currency (₦ for NGN, ₪ for ILS, ...), falling back to the code. */
  get symbol(): string {
    return CurrencyService.SYMBOLS[(this.code || '').toLowerCase()] || this.code;
  }

  /** Convert an ILS amount into the preferred currency (falls back to the ILS amount). */
  convertFromIls(amountIls: number): number {
    const a = amountIls || 0;
    if (this.preferred === 'ils') { return a; }
    const r = this.rate(this.preferred);
    return r ? a / r : a;
  }

  /** Convert an amount given in `fromCurrency` into the preferred currency (bridged via ILS). */
  convertToPreferred(amount: number, fromCurrency: string): number {
    const from = (fromCurrency || 'ils').toLowerCase();
    if (from === 'ils') { return this.convertFromIls(amount); }
    const r = this.rate(from);          // ILS per 1 unit of `from`
    const ils = r ? (amount || 0) * r : (amount || 0);
    return this.convertFromIls(ils);
  }
}
